import { Check, Sparkles, Clock, UserPlus, Heart, Eye, PlayCircle, Link2, Image as ImageIcon, Users, ThumbsUp, MessageCircle, Share2, Flame } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { useCurrency } from "../../contexts/CurrencyContext";
import { useLiveBuyerCount } from "../../lib/liveBuyerCount";
import { Button } from "../ui/button";

function getCategoryIcon(category = "") {
  const c = category.toLowerCase();
  if (c.includes("reaction")) return ThumbsUp;
  if (c.includes("comment")) return MessageCircle;
  if (c.includes("share")) return Share2;
  if (c.includes("like")) return Heart;
  if (c.includes("connection")) return Link2;
  if (c.includes("repin")) return ImageIcon;
  if (c.includes("view")) return Eye;
  if (c.includes("stream") || c.includes("play")) return PlayCircle;
  if (c.includes("subscriber") || c.includes("member")) return Users;
  return UserPlus;
}

const COUNTRY_FLAG = { France: "\u{1F1EB}\u{1F1F7}", Europe: "\u{1F1EA}\u{1F1FA}" };

export default function PricingCard({ product, meta, onSelect, selected }) {
  const { field, t } = useLanguage();
  const { format } = useCurrency();
  const liveBuyers = useLiveBuyerCount(product.id);
  const hasDiscount = product.compare_at_price && product.compare_at_price > product.price;
  const discountPct = hasDiscount ? Math.round((1 - product.price / product.compare_at_price) * 100) : 0;
  const CategoryIcon = getCategoryIcon(product.category);
  const accent = meta?.solid || "#6366f1";
  const flag = COUNTRY_FLAG[product.country_target];

  return (
    <div
      className={`group relative rounded-2xl p-6 pt-8 border transition-all cursor-pointer hover:-translate-y-1.5 hover:shadow-2xl ${
        selected ? "border-transparent bg-zinc-900" : "border-white/8 bg-zinc-900/60 hover:border-white/20"
      } ${product.popular ? "ring-2 ring-indigo-400/50 shadow-lg shadow-indigo-500/10" : ""}`}
      style={selected ? { boxShadow: `0 0 0 2px ${accent}, 0 12px 32px -8px ${accent}55` } : undefined}
      onClick={() => onSelect(product)}
      data-testid={`pricing-card-${product.id}`}
    >
      <div
        className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity -z-10"
        style={{ background: `radial-gradient(140% 100% at 0% 0%, ${accent}14, transparent 60%)` }}
      />

      <div className="flex items-center gap-2 absolute -top-3 left-6">
        {product.popular && (
          <span className="relative text-[10px] font-bold px-2.5 py-1 rounded-full bg-indigo-500 text-white flex items-center gap-1 shadow-lg shadow-indigo-500/40" data-testid={`pricing-recommended-badge-${product.id}`}>
            <span className="absolute inset-0 rounded-full bg-indigo-400 animate-ping opacity-40" />
            <Sparkles className="w-3 h-3 relative" /> <span className="relative">{t("common.recommended", "Recommandé")}</span>
          </span>
        )}
        {hasDiscount && (
          <span className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-rose-500 text-white shadow-lg shadow-rose-500/30" data-testid={`pricing-discount-badge-${product.id}`}>
            -{discountPct}%
          </span>
        )}
      </div>

      <div className="flex items-center justify-between mb-4">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110 group-hover:rotate-3"
          style={{ background: `linear-gradient(135deg, ${accent}33, ${accent}0d)`, border: `1px solid ${accent}40` }}
        >
          <CategoryIcon className="w-5 h-5" style={{ color: accent }} strokeWidth={2} />
        </div>
        {selected && (
          <div className="w-6 h-6 rounded-full flex items-center justify-center text-white" style={{ background: accent }} data-testid={`pricing-selected-check-${product.id}`}>
            <Check className="w-3.5 h-3.5" />
          </div>
        )}
      </div>

      <div className="font-heading text-2xl font-bold text-white">{product.quantity.toLocaleString()}</div>
      <div className="text-xs text-zinc-500 mb-4 flex items-center gap-1.5">
        {flag && <span data-testid={`pricing-flag-${product.id}`}>{flag}</span>}
        {field(product, "category_label")}
      </div>

      <div className="flex items-baseline gap-2 mb-1">
        <span className="font-heading text-3xl font-extrabold" style={{ color: accent }}>{format(product.price)}</span>
        {hasDiscount && <span className="text-sm text-zinc-500 line-through">{format(product.compare_at_price)}</span>}
      </div>
      <div className="flex items-center gap-1.5 text-xs text-zinc-500 mb-5">
        <Clock className="w-3.5 h-3.5" />
        {field(product, "delivery_time")}
      </div>

      {product.popular && (
        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-amber-400 mb-4" data-testid={`pricing-live-buyers-${product.id}`}>
          <Flame className="w-3.5 h-3.5" />
          {liveBuyers} {t("network.live_buyers_now", "personnes achètent ce pack en ce moment")}
        </div>
      )}

      <Button
        className={`w-full rounded-full transition-colors ${selected ? "text-white" : "bg-white/10 hover:bg-white/20 text-white"}`}
        style={selected ? { background: accent } : undefined}
        data-testid={`pricing-select-btn-${product.id}`}
      >
        {selected ? <><Check className="w-4 h-4 mr-1.5" />{t("common.select_pack", "Choisir ce pack")}</> : t("common.select_pack", "Choisir ce pack")}
      </Button>
    </div>
  );
}
